import { ReactNode, useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Button, Select, message } from "antd";
import dayjs from "dayjs";
import { useTask } from "../../queries/tasks";
import { useAddSubtask, useUpdateSubtask, useDeleteSubtask } from "../../queries/subtasks";
import { useUsers } from "../../queries/users";
import { TaskStatus, SubTaskProps, SessionUser } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { TaskStatusTag, TaskPriorityTag, StatusTag, TASK_STATUS } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import AuthContext from "../../context/AuthContext";
import styles from "./task.module.css";

const SUBTASK_STATUSES: TaskStatus[] = ["backlog", "ready", "in_progress", "in_review", "done"];

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={styles.fact}>
    <span className="q-kicker">{label}</span>
    <span className={styles.factValue}>{children}</span>
  </div>
);

const None = () => <span className={styles.factEmpty}>Not set</span>;

export const TaskDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "admin" || userData?.role === "user";
  const { data: task, isLoading, isError, error } = useTask(id!);
  const { data: users } = useUsers({ enabled: isStaff });
  const addSubtask = useAddSubtask(id!);
  const updateSubtask = useUpdateSubtask(id!);
  const deleteSubtask = useDeleteSubtask(id!);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) return;
    try {
      await addSubtask.mutateAsync({ title: newSubtaskTitle.trim() });
      setNewSubtaskTitle("");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not add the subtask");
    }
  };

  const userName = (uid: string | null) => (users || []).find((u: SessionUser) => u._id === uid)?.name;
  const done = (task?.subtasks || []).filter((s: SubTaskProps) => s.status === "done").length;
  const overdue = task?.dueDate && task.status !== "done" && dayjs(task.dueDate).isBefore(dayjs(), "day");

  return (
    <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
      {task && (
        <PageShell
          kind="detail"
          crumbs={[
            { label: "Projects", to: "/projects" },
            { label: "Board", to: `/projects/${task.projectId}/board` },
            { label: task._id.slice(-4).toUpperCase() },
          ]}
          title={task.title}
          meta={[
            { label: "Status", value: <TaskStatusTag status={task.status} /> },
            { label: "Priority", value: <TaskPriorityTag priority={task.priority} /> },
            ...(task.isBlocked ? [{ label: "Flag", value: <StatusTag label="Blocked" tone="danger" dot /> }] : []),
            { label: "Points", value: task.storyPoints != null ? <span className="q-num">{task.storyPoints}</span> : <None /> },
          ]}
          actions={
            <Button onClick={() => navigate(`/projects/${task.projectId}/board`)}>Back to board</Button>
          }
        >
          <div className={styles.split}>
            <div className={styles.main}>
              <div className="q-kicker" style={{ marginBottom: 8 }}>Description</div>
              <p className={styles.desc}>
                {task.description || "No description yet — add one before this moves to Ready."}
              </p>
            </div>
            <aside className={styles.rail}>
              <Fact label="Assignee">{userName(task.assigneeId) || <None />}</Fact>
              <Fact label="Reporter">{userName(task.reporterId) || <None />}</Fact>
              <Fact label="Due">
                {task.dueDate
                  ? <span className="q-num" style={{ color: overdue ? "var(--q-danger)" : undefined }}>
                      {dayjs(task.dueDate).format("D MMM YYYY")}
                    </span>
                  : <None />}
              </Fact>
              <Fact label="Labels">
                {task.labels?.length
                  ? <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {task.labels.map((l: string) => <StatusTag key={l} label={l} tone="neutral" />)}
                    </span>
                  : <None />}
              </Fact>
            </aside>
          </div>

          <section>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Subtasks</span>
              <span className={styles.sectionCount}>
                {task.subtasks.length ? `${done} of ${task.subtasks.length} done` : "none yet"}
              </span>
            </div>

            <div className={styles.subtasks}>
              {task.subtasks.length === 0 ? (
                <div style={{ padding: 24 }}>
                  <EmptyState
                    title="No subtasks"
                    description="Break the task down if it's more than a day's work — subtasks carry their own status."
                  />
                </div>
              ) : (
                task.subtasks.map((subtask: SubTaskProps) => (
                  <div className={styles.sub} key={subtask._id}>
                    <span className={`${styles.subTitle} ${subtask.status === "done" ? styles.subDone : ""}`}>
                      {subtask.title}
                    </span>
                    <Select
                      size="small"
                      style={{ width: 140 }}
                      value={subtask.status}
                      options={SUBTASK_STATUSES.map((s) => ({ label: TASK_STATUS[s].label, value: s }))}
                      onChange={(status) =>
                        updateSubtask.mutate(
                          { subtaskId: subtask._id, status },
                          { onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not update the subtask") }
                        )
                      }
                    />
                    <Button
                      size="small"
                      danger
                      onClick={() =>
                        deleteSubtask.mutate(subtask._id, {
                          onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not delete the subtask"),
                        })
                      }
                    >
                      Delete
                    </Button>
                  </div>
                ))
              )}
              <div className={styles.adder}>
                <Input
                  value={newSubtaskTitle}
                  placeholder="Add a subtask"
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onPressEnter={handleAddSubtask}
                />
                <Button type="primary" onClick={handleAddSubtask} loading={addSubtask.isPending}>Add</Button>
              </div>
            </div>
          </section>
        </PageShell>
      )}
    </DisplayComponent>
  );
};
